import React, {useEffect} from "react";
import {View} from "@unibuddy/patron";
import {useScreensizeContext} from "../providers/ScreensizeProvider";
import {usePostMessage} from "../hooks/usePostMessage";
import {useMobileMenu} from "../providers/MobileMenuProvider";

type FocusTrapProps = {
    children: React.ReactNode;
};

function FocusTrap({children}: FocusTrapProps) {
    const {postMessage} = usePostMessage();
    const {isMobileMenuOpen} = useMobileMenu();

    useEffect(() => {
        function keydownHandler(e: KeyboardEvent) {
            // If the escape key is pressed, either close the mobile menu or close the chat
            if (e.key === "Escape") {
                // If the mobile menu is open, do nothing. Escape key is handled internally
                // by the menu
                if (isMobileMenuOpen) return;

                postMessage("chatVisible=false");
                return;
            }

            // Ignore all keys except tab and shift + tab
            if (!e.shiftKey && e.key !== "Tab") {
                return;
            }

            const allFocusable = document.querySelectorAll(
                "button, a, input, select, textarea, [tabindex]",
            );

            const focusable = Array.from(allFocusable).filter(
                (element) => element.getAttribute("tabindex") !== "-1",
            );

            // Ignore if no focusable elements are found
            if (focusable && focusable.length === 0) {
                return;
            }

            const firstElement = focusable[0];
            const lastElement = focusable[focusable.length - 1];
            const isOnFirstElement = document.activeElement === firstElement;
            const isOnLastElement = document.activeElement === lastElement;

            // Ignore when shift + tab on the last element
            if (e.shiftKey && e.key === "Tab" && isOnLastElement) {
                return;
            }

            // Handle shift + tab on the first element
            if (e.shiftKey && e.key === "Tab" && isOnFirstElement) {
                e.preventDefault();
                (lastElement as HTMLElement).focus();
            }

            // Handle tab on the last element
            if (!e.shiftKey && e.key === "Tab" && isOnLastElement) {
                e.preventDefault();
                (firstElement as HTMLElement).focus();
            }
        }

        window.addEventListener("keydown", keydownHandler, true);

        return () => {
            window.removeEventListener("keydown", keydownHandler, true);
        };
    }, [postMessage, isMobileMenuOpen]);

    return <>{children}</>;
}

type AppWrapperProps = {
    children: React.ReactNode;
};

export const AppWrapper = ({children}: AppWrapperProps) => {
    const {isSmallDeviceDimension} = useScreensizeContext();

    return (
        <FocusTrap>
            <View
                borderRadius={isSmallDeviceDimension ? "none" : "small"}
                alignItems="center"
                justifyContent="center"
            >
                <View overflow="hidden" borderRadius="small" width="100%" height="100vh">
                    {children}
                </View>
            </View>
        </FocusTrap>
    );
};
